
import { defineComponent, computed, onMounted, onUnmounted, ref, ComponentPublicInstance } from "vue";
import store from "@/store";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { useLog } from "@/use/use-log";
import SelectCategory from "@/components/common/SelectCategory.vue";
import ShoppingListCategorySelectingIngredientList from "@/components/shoppingList/ShoppingListCategorySelectingIngredientList.vue";
import SelectingIngredientList from "@/components/common/SelectingIngredientList.vue";
import { SelectingIngredientListDataItem } from "@/types/common";
import {
  ShoppingListPantryItemsConsumeFoodList,
  ShoppingListRankingFoodList,
  ShoppingListFavoriteFoodList,
  ShoppingListRegistListItem,
  ShoppingListAppendFoodsCategoryListItem,
  ShoppingListCategorySeachParameter,
  ShoppingListSearchFoodResultList,
  ShoppingListSceneTopCategoryListItem,
  ShoppingListSceneCategoryListChildrenItem,
  ShoppingListAppendRegistingListItem
} from "@/types/shoppingList";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";
import { ApiError } from "@/types/common";
import ShoppingListSearchInputView from "@/components/shoppingList/ShoppingListSearchInputView.vue";
import router from "@/router";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";

/** スクロールイベントのIF */
interface HTMLElementEvent<T extends HTMLElement> extends Event {
  target: T;
}

export default defineComponent({
  name: "ShoppingListAppendIngredientPage",
  components: {
    SelectCategory, 
    ShoppingListCategorySelectingIngredientList, 
    ShoppingListSearchInputView, 
    CommonDesignDialog
  },
  setup() {
    const { setLogId, outputPageLogs } = useLog();
    NativeClient.sendDebugLog("ShoppingListAppendIngredientPage setup");

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "食材追加"));
    CommonUtil.scroll({ top: 0, left: 0 });

    const isValidFocus = false;

    // 入力した検索キーワード
    const selectKeyword = ref("");

    // ダミーコールバック
    const dummyCallback = () => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage dummyCallback");
    };

    // WebAPIのリクエスト状況（リトライを行うため）
    class ShoppingListAppendFoodPageAPIErrorDialogType {
      type: number; // 0:なし、1:一覧取得・更新、2:食材登録、3:登録完了、4:追加項目なし、5:チェック項目が消える注意喚起
      index: number;  // 一覧取得・更新の位置
      negativeCallback: () => void;
      positiveCallback: () => void;
      constructor(type: number, index: number, negativeCallback: () => void, positiveCallback: () => void) {
        this.type = type;
        this.index = index;
        this.negativeCallback = negativeCallback;
        this.positiveCallback = positiveCallback;
      }
    }
    const refAPIErrorDialogType = ref(new ShoppingListAppendFoodPageAPIErrorDialogType(0, 0, dummyCallback, dummyCallback));

    class ShoppingListAppendFoodPageDialogType {
      type: number; // 0:なし、1:一覧取得・更新、2:食材登録、3:登録完了、4:追加項目なし、5:チェック項目が消える注意喚起
      nagativeCallback: () => void;
      positiveCallback: () => void;
      constructor(type: number, nagativeCallback: () => void, positiveCallback: () => void) {
        this.type = type;
        this.nagativeCallback = nagativeCallback;
        this.positiveCallback = positiveCallback;
      }
    }

    const refMessageDialogType = ref(new ShoppingListAppendFoodPageDialogType(0, dummyCallback, dummyCallback));

    //
    // 画面遷移
    //
    // 検索画面へ
    const gotoSearchScreen = () => {
      const keyword = selectKeyword.value 
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage gotoSearchScreen keywords = " + keyword);
      store.dispatch("shoppingList/updateSearchResultList", null);
      router.push(`/shoppingListSearchResult?keyword=${keyword}`);
    };
    // 買い物リストTOPへ
    const returnPreviousPage = () => {
      router.go(-1);
    };

    //
    // 共通ダイアログ
    //
    const isShowDesignDialog = ref(false);
    const designDialogType = ref(0);
    const designDialogTitle = ref("");
    const designDialogMessage = ref("");
    const designDialogNegativeButtonLabel = ref("");
    const designDialogPositiveButtonLabel = ref("");
    const designDialogNegativeButtonAction = () => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage designDialogNegativeButtonAction");
      isShowDesignDialog.value = false;
      refMessageDialogType.value.nagativeCallback();
    };
    const designDialogPositiveButtonAction = () => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage designDialogNegativeButtonAction");
      isShowDesignDialog.value = false;
      refMessageDialogType.value.positiveCallback();
    }
    const showCompleteDialog = (callback: () => void) => {
      designDialogType.value = 13;
      designDialogTitle.value = "食材を追加しました。";
      designDialogMessage.value = "";
      designDialogNegativeButtonLabel.value = "閉じる";
      isShowDesignDialog.value = true;
      refMessageDialogType.value = new ShoppingListAppendFoodPageDialogType(3, callback, dummyCallback);
    };

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    const updateCategoryKey = ref(0);
    const updateCategory = () => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage updateCategory");
      if (updateCategoryKey.value == 100) {
        updateCategoryKey.value = 0;
      } else {
        updateCategoryKey.value++;
      }
    };

    // 表示カテゴリ設定
    const selectCategoryIndex = ref(0);
    const initCategoryIdNameList: Array<ShoppingListSceneTopCategoryListItem> = [
      {id: -1, name: "消費済み"},
      {id: -1, name: "よく使う"},
      {id: -1, name: "お気に入り"}
    ]
    const categoryIdNameList = ref(initCategoryIdNameList);
    const categoryNameList = computed(() => {
      return categoryIdNameList.value.map(item => item.name);
    });
    const updateCategoryIdNameList = () => {
      const topCategoryList: Array<ShoppingListSceneTopCategoryListItem> | null = store.getters["shoppingList/getTopCategoryList"];
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage updateCategoryIdNameList() topCategoryList = " + JSON.stringify(topCategoryList));
      if (topCategoryList != null) {
        for(const item of topCategoryList) {
          const findIndex = categoryIdNameList.value.findIndex(element => element.id == item.id);
          if (findIndex == -1) {
            categoryIdNameList.value.push(item);
          }
        }
        NativeClient.sendDebugLog("ShoppingListAppendIngredientPage updateCategoryIdNameList() update result = " + JSON.stringify(categoryIdNameList));
        updateCategory();
      }
    };
    // スクロール
    const scroll = ref<ComponentPublicInstance<HTMLInputElement>>();
    const setScrollTo = (position: number) => {
      let categoryCount = 3;
      const topCategoryList: Array<ShoppingListSceneTopCategoryListItem> | null = store.getters["shoppingList/getTopCategoryList"];
      if (topCategoryList != null) {
        categoryCount += topCategoryList.length;
      }
      if (scroll.value != undefined) {
        NativeClient.sendDebugLog("ShoppingListAppendIngredientPage setScrollTo left = " + (scroll.value!.scrollWidth / categoryCount) * position);
        scroll.value!.scrollTo({ top: 0, left: (scroll.value!.scrollWidth / categoryCount) * position });
      }
    };

    class ShoppingListCategoryIngredientListItem {
      index: number;
      totalCount: number;
      ingredientList: Array<SelectingIngredientListDataItem> | null;
      constructor() {
        this.index = 0;
        this.totalCount = 0;
        this.ingredientList = null;
      }
    }
    const categoryIngredientList = computed(() => {
      const result: Array<ShoppingListCategoryIngredientListItem> = [];
      const registArrayOriginal: Array<ShoppingListAppendRegistingListItem> | null = store.getters["shoppingList/getRegistingFoodList"];
      const registArray: Array<ShoppingListAppendRegistingListItem> = registArrayOriginal == null ? [] : registArrayOriginal;
      
      // 消費済
      const cunsumedCategory = new ShoppingListCategoryIngredientListItem();
      cunsumedCategory.index = 0;
      const consumedList: ShoppingListPantryItemsConsumeFoodList | null = store.getters["shoppingList/getConsumedList"];
      if (consumedList != null) {
        cunsumedCategory.totalCount = consumedList.totalCount;
        cunsumedCategory.ingredientList = [];
        for (const item of consumedList.pantryList) {
          const resultElement = new SelectingIngredientListDataItem();
          if (item.ingredientId != undefined && item.ingredientId != null) {
            resultElement.id = item.ingredientId;
          } else {
            resultElement.id = null;
          }
          if (item.imageUrl != undefined && item.imageUrl != null) {
            resultElement.imageUrl = item.imageUrl;
          } else {
            resultElement.imageUrl = "";
          }
          resultElement.name = item.name;
          if (registArray.find(element => element.name == item.name && element.pageIndex == 0)) {
            NativeClient.sendDebugLog("ShoppingListAppendIngredientPage categoryData hit name = " + item.name);
            resultElement.isSelect = true;
          } else {
            resultElement.isSelect = false;
          }
          cunsumedCategory.ingredientList.push(resultElement);
        }
      }
      result.push(cunsumedCategory);

      // よく使う
      const rankingCategory = new ShoppingListCategoryIngredientListItem();
      rankingCategory.index = 1;
      const rankingList: ShoppingListRankingFoodList | null = store.getters["shoppingList/getRankingList"];
      if (rankingList != null) {
        rankingCategory.ingredientList = [];
        rankingCategory.totalCount = rankingList.foods.length;
        for (const item of rankingList.foods) {
          const resultElement = new SelectingIngredientListDataItem();
          resultElement.id = item.foodId;
          if (item.image != undefined && item.image != null) {
            resultElement.imageUrl = item.image;
          } else {
            resultElement.imageUrl = "";
          }
          resultElement.name = item.name;
          if (registArray.find(element => element.foodId == item.foodId && element.pageIndex == 1)) {
            resultElement.isSelect = true;
          } else {
            resultElement.isSelect = false;
          }
          rankingCategory.ingredientList.push(resultElement);
        }
      }
      result.push(rankingCategory);

      // お気に入り
      const favoriteCategory = new ShoppingListCategoryIngredientListItem();
      favoriteCategory.index = 2;
      const favoriteList: ShoppingListFavoriteFoodList | null = store.getters["shoppingList/getFavoriteList"];
      if (favoriteList != null) {
        if (favoriteList.totalCount != null) {
          favoriteCategory.totalCount = favoriteList.totalCount;
        }
        favoriteCategory.ingredientList = [];
        for (const item of favoriteList.foods) {
          const resultElement = new SelectingIngredientListDataItem();
          resultElement.id = item.foodId;
          if (item.image != undefined && item.image != null) {
            resultElement.imageUrl = item.image;
          } else {
            resultElement.imageUrl = "";
          }
          resultElement.name = item.name;
          if (registArray.find(element => element.foodId == item.foodId && element.pageIndex == 2)) {
            resultElement.isSelect = true;
          } else {
            resultElement.isSelect = false;
          }
          favoriteCategory.ingredientList.push(resultElement);
        }
      }
      result.push(favoriteCategory);

      // その他
      const topCategoryList: Array<ShoppingListSceneTopCategoryListItem> | null = store.getters["shoppingList/getTopCategoryList"];
      if (topCategoryList != null) {
        let currentIndex = 3;
        for (const topCategory of topCategoryList) {
          const currentCategory = new ShoppingListCategoryIngredientListItem();
          currentCategory.index = currentIndex;
          currentCategory.ingredientList = [];
          const sceneIngredientList: Array<ShoppingListSceneCategoryListChildrenItem> | null = store.getters["shoppingList/getCategoryFooodList"](topCategory.id);
          if (sceneIngredientList != null) {
            for (const item of sceneIngredientList) {
              const resultElement = new SelectingIngredientListDataItem();
              resultElement.id = item.id;
              if (item.image != undefined && item.image != null) {
                resultElement.imageUrl = item.image;
              } else {
                resultElement.imageUrl = "";
              }
              resultElement.name = item.name;
              if (registArray.find(element => element.foodId == item.id && element.pageIndex == currentIndex)) {
                resultElement.isSelect = true;
              } else {
                resultElement.isSelect = false;
              }
              currentCategory.ingredientList.push(resultElement);
            }
            currentIndex ++;
            result.push(currentCategory);
          }
        }
      }
      return result;
    });

    //
    // API実行
    //
    // 消費済みリスト取得
    const getConsumedList = async (isChangeCategory: boolean) => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage getConsumedList isChangeCategory = " + isChangeCategory);
      const data: ShoppingListPantryItemsConsumeFoodList | null = store.getters["shoppingList/getConsumedList"];
      if (isChangeCategory && data != null) {
        updateDisplay();
        // 既に読み込み済みなので、読み込まない
        return;
      }
      refAPIErrorDialogType.value = new ShoppingListAppendFoodPageAPIErrorDialogType(1, 0, dummyCallback, dummyCallback);
      const topCategoryList: Array<ShoppingListSceneTopCategoryListItem> | null = store.getters["shoppingList/getTopCategoryList"];
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        if (topCategoryList == null) {
          // カテゴリーも一緒に読み込む
          await Promise.all([
            store.dispatch("shoppingList/getConsumedFoodList"),
            store.dispatch("shoppingList/getSceneCategoryList")
          ]);
          updateCategoryIdNameList();
        } else {
          await store.dispatch("shoppingList/getConsumedFoodList")
        }
        NativeClient.sendDebugLog("ShoppingListAppendIngredientPage getConsumedList() complete");
        updateDisplay();
        store.dispatch("common/stopLoading");
      }
    };
    // よく使うリスト取得
    const getRankingList = async (isChangeCategory: boolean) => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage getRankingList isChangeCategory = " + isChangeCategory);
      const data: ShoppingListRankingFoodList | null = store.getters["shoppingList/getRankingList"];
      if (isChangeCategory && data != null) {
        // 既に読み込み済みなので、読み込まない
        updateDisplay();
        return;
      }
      refAPIErrorDialogType.value = new ShoppingListAppendFoodPageAPIErrorDialogType(1, 1, dummyCallback, dummyCallback);
      const topCategoryList: Array<ShoppingListSceneTopCategoryListItem> | null = store.getters["shoppingList/getTopCategoryList"];
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        if (topCategoryList == null) {
          // カテゴリーも一緒に読み込む
          await Promise.all([
            store.dispatch("shoppingList/getRankingList"),
            store.dispatch("shoppingList/getSceneCategoryList")
          ])
          updateCategoryIdNameList();
        } else {
          await store.dispatch("shoppingList/getRankingList");
        }
        updateDisplay();
      }
      store.dispatch("common/stopLoading");
    };
    // お気に入り取得
    const getFavoriteList = async (isChangeCategory: boolean) => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage getFavoriteList isChangeCategory = " + isChangeCategory);
      const data: ShoppingListFavoriteFoodList | null = store.getters["shoppingList/getFavoriteList"];
      if (isChangeCategory && data != null) {
        // 既に読み込み済みなので、読み込まない
        updateDisplay();
        return;
      }
      refAPIErrorDialogType.value = new ShoppingListAppendFoodPageAPIErrorDialogType(1, 2, dummyCallback, dummyCallback);
      const topCategoryList: Array<ShoppingListSceneTopCategoryListItem> | null = store.getters["shoppingList/getTopCategoryList"];
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        if (topCategoryList == null) {
          // カテゴリーも一緒に読み込む
          await Promise.all([
            store.dispatch("shoppingList/getFavoriteList"),
            store.dispatch("shoppingList/getSceneCategoryList")
          ])
          updateCategoryIdNameList();
        } else {
          await store.dispatch("shoppingList/getFavoriteList");
        }
        updateDisplay();
      }
      store.dispatch("common/stopLoading");
    };
    // 食材登録
    const registFood = async (registInfo: Array<ShoppingListRegistListItem>, completion: () => void) => {
      store.dispatch("common/startLoading");
      await NativeClient.getApiParams();
      refAPIErrorDialogType.value = new ShoppingListAppendFoodPageAPIErrorDialogType(2, 0, completion, dummyCallback);
      if (isValidApiAccessToken()) {
        // 登録
        //refDialogType.value = new ShoppingListPageRequestType(6, 0, name, false);
        await store.dispatch("shoppingList/registShoppingList", registInfo);
      }
      store.dispatch("common/stopLoading");

      // チェック済削除
      store.dispatch("shoppingList/updateRegistingFoodList", null)

      showCompleteDialog(completion);
    };

    // 
    // カテゴリー選択
    //
    const changeCategory = (index: number) => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage changeCategory change index = " + index);
      switch (index) {
        case 0: /** 消費済み */
          getConsumedList(true);
          break;
        case 1: /** よく使う */
          getRankingList(true);
          break;
        case 2: /** お気に入り */
          getFavoriteList(true);
          break;
        default:
          // 既に取得済
          updateDisplay();
          break;
      }
      setScrollTo(index);
    };
    const selectCategory = (index: number) => {
      updateDisplay();
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage selectCategory select index = " + index);
      if (selectCategoryIndex.value != index) {
        selectCategoryIndex.value = index;
        changeCategory(index);
        store.dispatch("shoppingList/updateSelectCategoryIndex", index);
      }
    };

    const checkAction = (index: number, id: number | null, name: string, isSelect: boolean) => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage checkAction id = " + id + ", name = " + name + ", isSelect = " + isSelect);
      const registArrayOriginal: Array<ShoppingListAppendRegistingListItem> | null = store.getters["shoppingList/getRegistingFoodList"];
      const registArray: Array<ShoppingListAppendRegistingListItem> = registArrayOriginal == null ? [] : registArrayOriginal;
      if (isSelect) {
        if (!registArray.find(element => element.foodId == id && element.name == name)) {
          // 存在しないので追加する
          const element = new ShoppingListAppendRegistingListItem(index, id, name);
          registArray.push(element);
        }
      } else {
        const index = registArray.findIndex(element => element.foodId == id && element.name == name);
        if (index != -1) {
          // 存在するので削除する
          registArray.splice(index,1);
        }
      }
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage selectFoodAction = " + JSON.stringify(registArray));
      // 記憶しておく
      store.dispatch("shoppingList/updateRegistingFoodList", registArray);
    };

    // 検索バーのフォーカス
    const selectAppendCallback = () => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage selectAppendCallback");
      const checkeList: Array<ShoppingListAppendRegistingListItem> | null = store.getters["shoppingList/getRegistingFoodList"];
      if (checkeList == null || checkeList.length == 0) {
        return;
      }
      registFood(checkeList, gotoSearchScreen);
    };
    const setKeyword = (keyword: string) => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage setKeyword keyword = " + keyword);
      if (keyword == undefined || keyword == null || keyword.length == 0) {
        return;
      }
      selectKeyword.value = keyword;
      const checkeList: Array<ShoppingListAppendRegistingListItem> | null = store.getters["shoppingList/getRegistingFoodList"];
      if (checkeList == null || checkeList.length == 0) {
        // 注意喚起なく画面遷移
        gotoSearchScreen();
        return;
      }
      refMessageDialogType.value = new ShoppingListAppendFoodPageDialogType(5, dummyCallback, selectAppendCallback)
      designDialogType.value = 18;
      designDialogTitle.value = "";
      designDialogMessage.value = "選択済の食材を追加してから検索します。\n追加してよろしいですか？";
      designDialogNegativeButtonLabel.value = "キャンセル";
      designDialogPositiveButtonLabel.value = "追加する";
      isShowDesignDialog.value = true;
    };

    //
    // 追加ボタン
    //
    const appendButtonText = "追加する";
    const styleAppendButton = computed(() => {
      const appendInfo: Array<ShoppingListAppendRegistingListItem> | null = store.getters["shoppingList/getRegistingFoodList"];
      if (appendInfo == null || appendInfo.length == 0) {
        // 未チェック
        return { backgroundColor: `rgba(0,0,0,0.3)`};
      }
      return { backgroundColor: `#333333`};
    });
    const appendButtonAction = () => {
      const appendInfo: Array<ShoppingListAppendRegistingListItem> | null = store.getters["shoppingList/getRegistingFoodList"];
      if (appendInfo == null || appendInfo.length == 0) {
        // ボタンのディセーブル中
        return;
      } else {
        NativeClient.sendDebugLog("ShoppingListAppendIngredientPage appendButtonAction appendInfo = " + JSON.stringify(appendInfo));
        const list: Array<ShoppingListRegistListItem> = [];
        for (const info of appendInfo) {
          const item = new ShoppingListRegistListItem(info.foodId, info.name);
          list.push(item);
        }
        registFood(list, returnPreviousPage);
      }
    };

    //
    // 本ページの表示／非表示
    //
    let isDisplayed = false;
    onMounted(() => {
      isDisplayed = true;
      const previousCategoryIndex = store.getters["shoppingList/getPreviousSelectCategoryIndex"];
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage onMounted previousCategoryIndex = " + previousCategoryIndex);
      if (selectCategoryIndex.value == previousCategoryIndex) {
        // この場合は、データ読み込みを行なってくれないので、以下を実装する
        NativeClient.sendDebugLog("ShoppingListAppendIngredientPage onMounted calling changeCategory");
        updateCategoryIdNameList();
        updateCategory();
        changeCategory(previousCategoryIndex);
      } else {
        // selectCategoryで読み込みを呼び出す
        NativeClient.sendDebugLog("ShoppingListAppendIngredientPage onMounted calling selectCategory");
        updateCategoryIdNameList();
        updateCategory();
        selectCategory(previousCategoryIndex);
      }
    });
    onUnmounted(() => {
      isDisplayed = false;
    });

    //
    // 追加読み込み判定
    //
    const receiveRequestRead = (index: number) => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage requestRead index = " + index);
      switch (index) {
        case 0: // 消費済み
          {
            const list: ShoppingListPantryItemsConsumeFoodList | null = store.getters["shoppingList/getConsumedList"];
            if (list != null && list.pantryList.length < list.totalCount) {
              // 追加読み込み
              NativeClient.sendDebugLog("ShoppingListAppendIngredientPage onScroll consumedList more read");
              getConsumedList(false);
            }
          }
          break;
        case 1: // よく使う
          // これは追加読み込みなし
          break;
        case 2: // お気に入り
          {
            const list: ShoppingListFavoriteFoodList | null = store.getters["shoppingList/getFavoriteList"];
            if (list != null && list.totalCount != null && list.foods.length < list.totalCount) {
              // 追加読み込み
              NativeClient.sendDebugLog("ShoppingListAppendIngredientPage onScroll favoriteList more read");
              getFavoriteList(false);
            }
          }
          break;
        default:
          // これは追加読み込みなし
          break; 
      }
    };

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const isShowApiErrorDialog = computed(() => {
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage isShowApiErrorDialog");
      const apiError: ApiError = store.getters["common/getApiError"];
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage isShowApiErrorDialog showApiError");
      if (apiError == null) {
        NativeClient.sendDebugLog("ShoppingListAppendIngredientPage isShowApiErrorDialog no error");
        return false;
      }
      NativeClient.sendDebugLog("ShoppingListAppendIngredientPage isShowApiErrorDialog error");
      return true;
    });
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    const okApiErrorHandler = () => {
      closeDialog();
      refAPIErrorDialogType.value = new ShoppingListAppendFoodPageAPIErrorDialogType(0, 0, dummyCallback, dummyCallback);
    };
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
      // NativeClient.sendDebugLog("ShoppingListAppendIngredientPage retryApiErrorHandler");
      // switch (refAPIErrorDialogType.value.type) {
      //   case 1:
      //     NativeClient.sendDebugLog("ShoppingListAppendIngredientPage retryApiErrorHandler type = 1");
      //     switch (refAPIErrorDialogType.value.index) {
      //       case 0: // 消費済み
      //         getConsumedList(false);
      //         break;
      //       case 1: // よく使う
      //         getRankingList(false);
      //         break;
      //       case 2: // お気に入り
      //         getFavoriteList(false);
      //         break;
      //       default:
      //         NativeClient.sendDebugLog("ShoppingListAppendIngredientPage retryApiErrorHandler category");
      //         break;
      //     }
      //     break;
      //   case 2:
      //     NativeClient.sendDebugLog("ShoppingListAppendIngredientPage retryApiErrorHandler type = 2");
      //     {
      //       const checkeList: Array<ShoppingListAppendRegistingListItem> | null = store.getters["shoppingList/getRegistingFoodList"];
      //       if (checkeList == null || checkeList.length == 0) {
      //         return;
      //       }
      //       registFood(checkeList, refAPIErrorDialogType.value.negativeCallback);
      //     }
      //     break;
      //   default:
      //     NativeClient.sendDebugLog("ShoppingListAppendIngredientPage retryApiErrorHandler type = " + refAPIErrorDialogType.value.type);
      //     break;
      // }
    };

    return {
      selectCategoryIndex,
      updateCategoryKey,
      updateDisplayKey,
      isValidFocus,
      setKeyword,
      receiveRequestRead,
      categoryNameList,
      categoryIngredientList,
      selectCategory,
      scroll,
      checkAction,
      appendButtonText,
      styleAppendButton,
      appendButtonAction,
      handleApiError,
      isShowApiErrorDialog,
      typeApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler,
      isShowDesignDialog,
      designDialogType,
      designDialogTitle,
      designDialogMessage,
      designDialogNegativeButtonLabel,
      designDialogNegativeButtonAction,
      designDialogPositiveButtonLabel,
      designDialogPositiveButtonAction
    };
  }
});
